import { FC, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@screentone/addon-auth-wrapper';
import { Box, Breadcrumbs, Button, Group, Loader, Typography } from '@screentone/core';

import {
  AllessehContentType,
  ScheduledContentStatus,
  useGetArtifactsByPublicationKeyForContentParametersQuery
} from 'data/generated/graphql';
import { useConvertedProperty } from 'hooks';
import useContentParametersCatalog from 'hooks/contentParametersMapping/useContentParametersCatalog';
import { useIsAdmin, useIsSuper } from 'hooks/roles';
import { reportError } from 'utils/errorBoundary';
import styles from './ContentParameterSectionList.module.scss';

const PAGES_PER_REQUEST = 100;

const ContentParameterSectionList: FC = () => {
  const { data, error } = useContentParametersCatalog();
  const { currentPropertyObject } = useAuth();
  const isAdmin = useIsAdmin();
  const isSuper = useIsSuper();
  const canViewArtifactId = isAdmin || isSuper;

  const currentProperty = useConvertedProperty();
  const {
    data: artifactsData,
    error: artifactsError,
    isFetching: isFetchingArtifacts
  } = useGetArtifactsByPublicationKeyForContentParametersQuery(
    {
      publicationKey: currentProperty!,
      contentType: AllessehContentType.Page,
      status: ScheduledContentStatus.Live,
      paginationParams: { first: PAGES_PER_REQUEST }
    },
    {
      enabled: !!currentProperty,
      staleTime: 60000 * 10 // Cache for 10 minutes, as page name will almost never change
    }
  );

  const artifactNames = useMemo(() => {
    if (artifactsData) {
      return Object.fromEntries(
        artifactsData.scheduledContentByPublicationKey.edges.map((edge) => [
          edge.node.allessehId,
          (edge.node.body as { metadata: { name: string } }).metadata.name
        ])
      );
    }

    return {};
  }, [artifactsData]);

  useEffect(() => {
    if (artifactsError) {
      reportError('Could not load live versions for pages. Page names will not be shown.', artifactsError as Error);
    }
  }, [artifactsError]);

  let sectionList = (
    <div className={styles.loaderView}>
      <Loader size="lg" />
      <Typography
        margin={{
          top: 'md'
        }}
        variant="bodytext"
      >
        Loading list of sections...
      </Typography>
    </div>
  );

  if (data) {
    const artifactIds = data.getResolvedContentParametersCatalog.allessehIdList;

    sectionList =
      artifactIds.length === 0 ? (
        <Typography variant="header2">No section pages found for {currentPropertyObject?.name}</Typography>
      ) : (
        <>
          <Typography margin={{ bottom: 'lg', top: 'mlg' }} variant="header3">
            Section Pages
          </Typography>
          <Group fullWidth gap="mlg">
            {artifactIds.map((artifactId) => (
              <Box key={artifactId} className={styles.sectionBox}>
                <Box.Title>
                  {isFetchingArtifacts ? (
                    <Group align="center">
                      <Loader size="md" />
                    </Group>
                  ) : (
                    <Button componentEl={Link} className={styles.allessehIdButton} tertiary to={`${artifactId}`}>
                      {artifactNames[artifactId] ? artifactNames[artifactId] : artifactId}
                    </Button>
                  )}
                </Box.Title>
                {!isFetchingArtifacts && !artifactsError && canViewArtifactId && (
                  <Box.Content>{artifactId}</Box.Content>
                )}
              </Box>
            ))}
          </Group>
        </>
      );
  }

  if (error) {
    let errorText = <Typography variant="bodytext">Error loading section list page</Typography>;
    if (error instanceof Error) {
      errorText = <Typography variant="bodytext">Error loading section list page: {error.message}</Typography>;
    }

    sectionList = (
      <>
        <Typography variant="header2">Error</Typography>
        {errorText}
      </>
    );
  }

  return (
    <>
      <Breadcrumbs>
        <Breadcrumbs.Item
          componentEl={Link}
          to={`/${currentProperty}/site-config/mobile-and-app`}
          className={styles.propertyBreadcrumb}
        >
          {currentProperty}
        </Breadcrumbs.Item>
        <Breadcrumbs.Item componentEl={Link} to={`/${currentProperty}/site-config/mobile-and-app/content-presentation/list`}>
          Section List
        </Breadcrumbs.Item>
      </Breadcrumbs>
      {sectionList}
    </>
  );
};

export default ContentParameterSectionList;
